import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {ONE_DAY, ONE_MINUTE} from '#/lib/constants'
import {
  type ContentSource,
  type GetContentSourceRes,
} from '#/lib/wsoc/contentSource'
import {contentSourceService} from '#/lib/wsoc/contentSource'
import {useAgent, useSession} from '#/state/session'
import {CONTENT_SOURCE_ALLOWED_DIDS} from '#/env'

// ─── query keys ──────────────────────────────────────────────────────────────

export const CONTENT_SOURCE_CAPABILITY_KEY = 'content-source-capability'
export const CONTENT_SOURCE_RQKEY = (did: string) =>
  ['content-source', did] as const

// ─── feature flag ────────────────────────────────────────────────────────────

/**
 * Returns true when the PDS reports contentSourceEnabled AND the current user
 * passes the optional DID allowlist (EXPO_PUBLIC_CONTENT_SOURCE_ALLOWED_DIDS).
 * When the allowlist is empty every user is allowed.
 */
export function useContentSourceEnabled(): boolean {
  const agent = useAgent()
  const {currentAccount} = useSession()
  const {data} = useQuery({
    queryKey: [CONTENT_SOURCE_CAPABILITY_KEY],
    queryFn: () => agent.account.getCapabilities(),
    enabled: !!currentAccount,
    staleTime: ONE_DAY,
    gcTime: ONE_DAY,
  })
  if (!(data?.contentSourceEnabled ?? false)) return false
  if (CONTENT_SOURCE_ALLOWED_DIDS.length === 0) return true
  return (
    !!currentAccount?.did &&
    CONTENT_SOURCE_ALLOWED_DIDS.includes(currentAccount.did)
  )
}

// ─── read current source ─────────────────────────────────────────────────────

export function useContentSourceQuery() {
  const agent = useAgent()
  const {currentAccount} = useSession()
  const did = currentAccount?.did ?? ''

  return useQuery<GetContentSourceRes>({
    queryKey: CONTENT_SOURCE_RQKEY(did),
    queryFn: ({signal}) => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')
      return contentSourceService.fetch(accessJwt, {signal})
    },
    enabled: !!currentAccount,
    staleTime: ONE_MINUTE * 5,
    // Seed from MMKV so UI shows instantly on first render
    placeholderData: () => {
      const cached = contentSourceService.getCached(did)
      if (!cached) return undefined
      return {
        source: cached,
        explicit: true,
        available: ['federated', 'wsocial'] as ContentSource[],
      }
    },
  })
}

// ─── set source ──────────────────────────────────────────────────────────────

export function useSetContentSourceMutation() {
  const agent = useAgent()
  const {currentAccount} = useSession()
  const queryClient = useQueryClient()

  return useMutation<void, Error, ContentSource>({
    mutationFn: async (source: ContentSource) => {
      const accessJwt = agent.session?.accessJwt
      const did = currentAccount?.did
      if (!accessJwt || !did) throw new Error('Not authenticated')
      await contentSourceService.set(accessJwt, source, did, queryClient)
    },
    onSuccess: (_data, source) => {
      const did = currentAccount?.did
      if (!did) return
      // Re-seed the query with the new value immediately so the UI is responsive
      queryClient.setQueryData<GetContentSourceRes>(
        CONTENT_SOURCE_RQKEY(did),
        old =>
          old
            ? {...old, source, explicit: true}
            : {
                source,
                explicit: true,
                available: ['federated', 'wsocial'] as ContentSource[],
              },
      )
    },
  })
}
